import { c as _c } from "react/compiler-runtime";
import { GroupsDemoEmptyPage } from "@components/common/empty-page/groups-demo-empty-page";
import { GroupsEmptyPage } from "@components/common/empty-page/groups-empty-page";
import { PageContainer } from "@components/common/page-container";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { DEFAULT_GROUPS_SORT_BY } from "@components/groups/groups-constants";
import { GroupsSort } from "@components/groups/groups-sort";
import { BulkExportGroups } from "@components/table/groups/bulk-actions/bulk-export-groups";
import { isGroupsSortBy } from "@custom-types/type-guards";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useGroups } from "@hooks/use-groups";
import { useSubjectsSortByQuery } from "@hooks/use-subjects-sort-by-query";
import { GroupsActionButtons } from "@pages/groups/groups-action-buttons";
import { CreateGroup } from "@components/groups/create-group";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useMediaQueryList } from "@hooks/use-media-query";
import { GroupsMobileView } from "@pages/groups/groups-mobile-view";
import { fetchingGroupsFlagsSelector, filteredCompanyGroupsSelector } from "@store/groups/groups-selector";
import { useAppSelector } from "@store/store-helper";
import { dataViewModeSelector } from "@store/ui/ui-selector";
import { isGroupDemo, sortGroups } from "@utils/group-utils";
import { useMemo } from "react";
import { GroupsPageTable } from "@pages/groups/groups-page-table";
import { GroupsTiles } from "@pages/groups/groups-tiles";

/** Content in the Groups page */
export function GroupsPage() {
  const $ = _c(47);
  const companyId = useCompanyId();
  const groups = useGroups();
  const filteredCompanyGroups = useAppSelector(filteredCompanyGroupsSelector);
  const {
    groups: viewMode
  } = useAppSelector(dataViewModeSelector);
  const {
    isFetchingGroups
  } = useAppSelector(fetchingGroupsFlagsSelector);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      defaultSort: DEFAULT_GROUPS_SORT_BY,
      isValidSort: isGroupsSortBy
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    sortBy,
    setSortBy
  } = useSubjectsSortByQuery(t0);
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const {
    canCreateGroups
  } = useHasUserValidRoleCompanyLevel();
  let t1;
  if ($[1] !== filteredCompanyGroups || $[2] !== sortBy) {
    t1 = sortGroups(filteredCompanyGroups, sortBy);
    $[1] = filteredCompanyGroups;
    $[2] = sortBy;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const sortedGroups = t1;
  if (groups.length === 1 && isGroupDemo(groups[0]) && filteredCompanyGroups.length === 1) {
    let t2;
    if ($[4] !== companyId || $[5] !== filteredCompanyGroups[0]) {
      t2 = <GroupsDemoEmptyPage companyId={companyId} demoGroup={filteredCompanyGroups[0]} />;
      $[4] = companyId;
      $[5] = filteredCompanyGroups[0];
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  const shouldShowEmptyPage = filteredCompanyGroups.length === 0 && !isFetchingGroups;
  let t2;
  if ($[7] !== companyId || $[8] !== isFetchingGroups || $[9] !== setSortBy || $[10] !== sortBy || $[11] !== sortedGroups) {
    t2 = <GroupsPageTable companyId={companyId} groups={sortedGroups} isLoading={isFetchingGroups} sortBy={sortBy} setSortBy={setSortBy} />;
    $[7] = companyId;
    $[8] = isFetchingGroups;
    $[9] = setSortBy;
    $[10] = sortBy;
    $[11] = sortedGroups;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let groupsContent = t2;
  if (viewMode === "cards" && !isScreenSmOrSmaller) {
    let t3;
    if ($[13] !== sortedGroups) {
      t3 = <GroupsTiles groups={sortedGroups} />;
      $[13] = sortedGroups;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    groupsContent = t3;
  }
  const shouldRenderActions = sortedGroups.length > 0;
  let t3;
  if ($[15] !== groups || $[16] !== isScreenSmOrSmaller) {
    t3 = isScreenSmOrSmaller ? undefined : <GroupsActionButtons groups={groups} />;
    $[15] = groups;
    $[16] = isScreenSmOrSmaller;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== canCreateGroups || $[19] !== groups || $[20] !== shouldShowEmptyPage) {
    t4 = !shouldShowEmptyPage && canCreateGroups && <CreateGroup groups={groups} />;
    $[18] = canCreateGroups;
    $[19] = groups;
    $[20] = shouldShowEmptyPage;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] !== canAssignManagers || $[23] !== setSortBy || $[24] !== shouldRenderActions || $[25] !== sortBy) {
    t5 = shouldRenderActions && <GroupsSort key="groups-sort" sortBy={sortBy} setSortBy={setSortBy} hiddenFields={canAssignManagers ? undefined : ["members"]} />;
    $[22] = canAssignManagers;
    $[23] = setSortBy;
    $[24] = shouldRenderActions;
    $[25] = sortBy;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== groups || $[28] !== shouldRenderActions) {
    t6 = shouldRenderActions && <BulkExportGroups key="BulkExportGroups" selectedGroups={groups} areEntitiesSelected={false} buttonType="standard" />;
    $[27] = groups;
    $[28] = shouldRenderActions;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  let t7;
  if ($[30] !== filteredCompanyGroups.length || $[31] !== isFetchingGroups || $[32] !== t4 || $[33] !== t5 || $[34] !== t6) {
    t7 = <EntitiesToolbar toolbarEntityName="Group" numberOfRows={filteredCompanyGroups.length} isLoading={isFetchingGroups} mobileAction={t4} sortActions={t5} exportComponent={t6} />;
    $[30] = filteredCompanyGroups.length;
    $[31] = isFetchingGroups;
    $[32] = t4;
    $[33] = t5;
    $[34] = t6;
    $[35] = t7;
  } else {
    t7 = $[35];
  }
  let t8;
  if ($[36] !== groupsContent || $[37] !== isFetchingGroups || $[38] !== isScreenSmOrSmaller || $[39] !== shouldShowEmptyPage || $[40] !== sortBy || $[41] !== sortedGroups) {
    t8 = shouldShowEmptyPage ? <GroupsEmptyPage /> : <>{isScreenSmOrSmaller && <GroupsMobileView groups={sortedGroups} sortBy={sortBy} isLoading={isFetchingGroups} />}{!isScreenSmOrSmaller && groupsContent}</>;
    $[36] = groupsContent;
    $[37] = isFetchingGroups;
    $[38] = isScreenSmOrSmaller;
    $[39] = shouldShowEmptyPage;
    $[40] = sortBy;
    $[41] = sortedGroups;
    $[42] = t8;
  } else {
    t8 = $[42];
  }
  let t9;
  if ($[43] !== t3 || $[44] !== t7 || $[45] !== t8) {
    t9 = <PageContainer title="Groups Management" dataTestId="GROUPS_CONTAINER" actionButtons={t3}>{t7}{t8}</PageContainer>;
    $[43] = t3;
    $[44] = t7;
    $[45] = t8;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  return t9;
}